import { DiscountInfo, DiscountInfoState } from './types'
import { DiscountTier, UpsellLocation } from './constants'
import { FETCH_PRINT_STORE_DISCOUNTS } from '@dtx-company/flow-codegen/src/code/queries'
import { FetchPrintStoreDiscountsResponseType } from '../../../types/print-store.types'
import { LockModalVariants } from '@dtx-company/inter-app/src/constants/lockModal'
import { PRINT_STORE_DISCOUNT_UPSELL } from '../../../constants/optimizely'
import { ProUpgradeSourceIds } from '../../../components/ProUpradeModal/ProUpgradeModal.types'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useMemo } from 'react'
import { useOpenProUpgradeModal } from '../../../components/ProUpradeModal/ProUpgradeModal.hooks'
import events from '@dtx-company/inter-app/src/event-tracking/events/printStore'
import useSWR from 'swr'

export const useFetchPrintStoreDiscounts = ():
  | {
      [key: string]: DiscountInfo
    }
  | null
  | undefined => {
  const { data, error } = useSWR<FetchPrintStoreDiscountsResponseType>(
    FETCH_PRINT_STORE_DISCOUNTS,
    {
      revalidateOnFocus: false
    }
  )

  if (error) {
    return null
  }

  if (data) {
    const discounts = data?.printStoreDiscountsByPlan

    const freeDiscount = discounts?.freeDiscount?.percent ?? 0
    const proDiscount = discounts?.proDiscount?.percent ?? 0
    const proplusDiscount = discounts?.proplusDiscount?.percent ?? 0
    const proflexDiscount = discounts?.proflexDiscount?.percent ?? 0
    const enterpriseDiscount = discounts?.enterpriseDiscount?.percent ?? 0

    const FREE = {
      discount: freeDiscount,
      tier: DiscountTier.FREE.tier,
      label: DiscountTier.FREE.label
    }
    const PRO = {
      discount: proDiscount,
      tier: DiscountTier.PRO.tier,
      label: DiscountTier.PRO.label
    }
    const PROPLUS = {
      discount: proplusDiscount,
      tier: DiscountTier.PROPLUS.tier,
      label: DiscountTier.PROPLUS.label
    }
    const PROFLEX = {
      discount: proflexDiscount,
      tier: DiscountTier.PROFLEX.tier,
      label: DiscountTier.PROFLEX.label
    }
    const ENTERPRISE = {
      discount: enterpriseDiscount,
      tier: DiscountTier.ENTERPRISE.tier,
      label: DiscountTier.ENTERPRISE.label
    }

    return {
      FREE,
      PRO,
      PROPLUS,
      PROFLEX,
      ENTERPRISE
    }
  }

  return undefined
}

const freeUpsellOperation = `view:print_store_free_to_pro_upsell`
const proUpsellOperation = `view:print_store_pro_to_pro_plus_upsell`

export const useDiscountInfo = (location?: UpsellLocation): DiscountInfoState => {
  let currentDiscount, upsell, openModal

  const [isStoreDiscountUpsellActive] = useFlowFeature(PRINT_STORE_DISCOUNT_UPSELL)

  const { deprecated_isEnterpriseUser: isEnterpriseTier, hasOperation } = useAuthState()

  const shouldUpsellFreeToPro = hasOperation(freeUpsellOperation)
  const shouldUpsellProToProPlus = hasOperation(proUpsellOperation)

  const isProflexTier = hasOperation('view:print_store_pro_flex_discount')
  const isProplusTier = hasOperation('view:print_store_pro_plus_discount')

  const lockModalVariant = useMemo(() => {
    if (shouldUpsellFreeToPro) return LockModalVariants.FC_PRINT_STORE_DISCOUNT_FREE_TO_PRO_UPSELL
    if (shouldUpsellProToProPlus)
      return LockModalVariants.FC_PRINT_STORE_DISCOUNT_PRO_TO_PROPLUS_UPSELL
  }, [shouldUpsellFreeToPro, shouldUpsellProToProPlus])

  const isUpsellableTier = Boolean(lockModalVariant)

  const { handleProtectedActionClick } = useOpenProUpgradeModal({
    lockModalVariant,
    sourceId: ProUpgradeSourceIds.PRINT_STORE_DISCOUNT_UPSELL,
    openProModalCondition: isStoreDiscountUpsellActive && isUpsellableTier,
    trackingCall: () => (location ? events.userClickedPrintStoreUpsell(location) : {}),
    action: () => window.location.reload() // need to refresh page after upsell complete
  })

  const res = useFetchPrintStoreDiscounts()
  if (isStoreDiscountUpsellActive && res) {
    openModal = handleProtectedActionClick
    const { FREE, PRO, PROPLUS, PROFLEX, ENTERPRISE } = res

    if (shouldUpsellFreeToPro) {
      currentDiscount = FREE
      upsell = res[DiscountTier.FREE.upsell]
    } else if (shouldUpsellProToProPlus) {
      currentDiscount = PRO
      upsell = res[DiscountTier.PRO.upsell]
    } else if (isProplusTier) {
      currentDiscount = PROPLUS
    } else if (isProflexTier) {
      currentDiscount = PROFLEX
    } else if (isEnterpriseTier) {
      currentDiscount = ENTERPRISE
    }
  }

  return { currentDiscount, upsell, openModal }
}
